/**
 * The rule engine behind `advisor`, kept out of the component on purpose.
 *
 * Nothing in this file renders, reads the DOM, or knows what the advisor is
 * advising about. It takes the stored questions and the answers given so far
 * and returns one resolved state: a score per dimension, a primary and an
 * optional secondary dimension, the quality tier, and the flags the answers
 * raised. The component asks it the same question after every answer, which is
 * what makes a conditional question possible — a question can be shown because
 * of where the scores already stand.
 */

export type AdvisorConditionOperator = "gte" | "lte" | "gt" | "lt" | "eq" | "ne";

/**
 * The one condition language. The same shape drives a conditional question
 * (`questions[].show_when`), a combined-route rule (`rules.combos[].when`) and
 * the matching of a result (`results[].when`), so an author learns it once.
 */
export type AdvisorCondition =
    | { all: AdvisorCondition[] }
    | { any: AdvisorCondition[] }
    | { not: AdvisorCondition }
    | { dimension: string; op?: AdvisorConditionOperator; value?: number; within_of_top?: number }
    | { primary: string }
    | { secondary: string }
    | { modifier: string }
    | { flag: string; is?: boolean }
    | { answer: string; is?: string; in?: string[] }
    | { tier: string }
    | { low_confidence: boolean };

export type AdvisorDimension = {
    id: number;
    key: string;
    label: string;
    description: string | null;
};

export type AdvisorTier = {
    id: number;
    key: string;
    label: string;
};

export type AdvisorOption = {
    id: number;
    key: string;
    label: string;
    why: string | null;
    scores: Record<string, number>;
    tier: string | null;
    flags: Record<string, boolean>;
};

export type AdvisorQuestion = {
    id: number;
    key: string;
    title: string;
    description: string | null;
    show_when: AdvisorCondition | null;
    show_in_site: boolean;
    options: AdvisorOption[];
};

export type AdvisorCombo = {
    id: number;
    when: AdvisorCondition | null;
    primary: string | null;
    secondary: string | null;
    modifier: string | null;
};

export type AdvisorRules = {
    strong_gap: number;
    low_confidence_max: number;
    tie_breaker_question: string | null;
    combos: AdvisorCombo[];
};

export type AdvisorCandidate = {
    id: number;
    tier: string | null;
    title: string;
    description: string | null;
    link: string | null;
    link_text: string | null;
};

export type AdvisorCta = {
    id: number;
    text: string;
    link: string | null;
    cta_type: string | null;
    variant: "primary" | "secondary";
};

export type AdvisorResult = {
    id: number;
    when: AdvisorCondition | null;
    title: string;
    description: string | null;
    status: string | null;
    color: string | null;
    points: string[];
    candidates: AdvisorCandidate[];
    ctas: AdvisorCta[];
    show_in_site: boolean;
};

export type AdvisorData = {
    dimensions: AdvisorDimension[];
    tiers: AdvisorTier[];
    questions: AdvisorQuestion[];
    rules: AdvisorRules;
    results: AdvisorResult[];
};

/** One answer, stored by question key so a reordered list keeps its answers. */
export type AdvisorAnswers = Record<string, string>;

export type AdvisorState = {
    scores: Record<string, number>;
    ranking: { key: string; score: number }[];
    primary: string | null;
    secondary: string | null;
    modifier: string | null;
    tier: string | null;
    flags: Record<string, boolean>;
    answers: AdvisorAnswers;
    low_confidence: boolean;
};

export const DEFAULT_RULES: AdvisorRules = {
    strong_gap: 3,
    low_confidence_max: 0,
    tie_breaker_question: null,
    combos: [],
};

/** A question the visitor can never answer is not part of the test. */
export function isUsableQuestion(question: AdvisorQuestion): boolean {
    return question?.show_in_site !== false
        && Array.isArray(question?.options)
        && question.options.length >= 2;
}

function optionOf(question: AdvisorQuestion, answers: AdvisorAnswers): AdvisorOption | null {
    const key = answers[question.key];
    if (!key) return null;
    return question.options.find(option => option.key === key) ?? null;
}

/**
 * Folds the answers into a state, then resolves the routes from it.
 *
 * `answered` is the ordered list of questions whose answers count. The caller
 * passes the path actually walked, so an answer left behind by a branch the
 * visitor backed out of never reaches the total.
 */
export function resolveState(
    data: AdvisorData,
    answered: AdvisorQuestion[],
    answers: AdvisorAnswers,
): AdvisorState {
    const dimensions = data.dimensions ?? [];
    const rules = { ...DEFAULT_RULES, ...(data.rules ?? {}) };

    const scores: Record<string, number> = {};
    dimensions.forEach(dimension => { scores[dimension.key] = 0; });

    const flags: Record<string, boolean> = {};
    let tier: string | null = null;

    answered.forEach(question => {
        const option = optionOf(question, answers);
        if (!option) return;

        Object.entries(option.scores ?? {}).forEach(([key, value]) => {
            if (!(key in scores)) return;
            scores[key] += Number(value) || 0;
        });

        Object.entries(option.flags ?? {}).forEach(([key, value]) => {
            if (value) flags[key] = true;
        });

        if (option.tier) tier = option.tier;
    });

    // Stable: equal scores keep the order the dimensions were authored in, so a
    // tie that no tie-breaker resolves still reads the same on every render.
    const ranking = dimensions
        .map((dimension, index) => ({ key: dimension.key, score: scores[dimension.key] ?? 0, index }))
        .sort((a, b) => (b.score - a.score) || (a.index - b.index))
        .map(({ key, score }) => ({ key, score }));

    const top = ranking[0] ?? null;
    const second = ranking[1] ?? null;

    let primary = top?.key ?? null;
    let secondary: string | null = null;
    let modifier: string | null = null;

    if (top && second) {
        const gap = top.score - second.score;

        if (gap === 0) {
            const tied = ranking.filter(entry => entry.score === top.score).map(entry => entry.key);
            const winner = breakTie(data, answers, rules, tied);
            primary = winner ?? tied[0];
            secondary = tied.find(key => key !== primary) ?? null;
        } else if (gap < Math.max(1, Number(rules.strong_gap) || 1)) {
            secondary = second.key;
        }
    }

    // Everything above is needed to evaluate a combo, so the base resolution
    // runs first and a combo overrides it — never the other way round.
    const base: AdvisorState = {
        scores,
        ranking,
        primary,
        secondary,
        modifier,
        tier,
        flags,
        answers,
        low_confidence: (top?.score ?? 0) < (Number(rules.low_confidence_max) || 0),
    };

    const combo = (rules.combos ?? []).find(entry => matchesCondition(entry?.when ?? null, base));
    if (combo) {
        if (combo.primary) primary = combo.primary;
        // A combo saying "these two belong together" must be able to drop the
        // alternative as well as set it, so an explicit empty string clears it.
        if (combo.secondary !== undefined && combo.secondary !== null) {
            secondary = combo.secondary === "" ? null : combo.secondary;
        }
        if (combo.modifier) modifier = combo.modifier;
    }

    return { ...base, primary, secondary, modifier };
}

/**
 * Picks a winner out of tied dimensions using one nominated question.
 *
 * The idea is that a visitor who stated a preference outright has already
 * answered the tie: the option they picked in that question scores one of the
 * tied dimensions higher than the others.
 */
function breakTie(
    data: AdvisorData,
    answers: AdvisorAnswers,
    rules: AdvisorRules,
    tied: string[],
): string | null {
    if (!rules.tie_breaker_question || tied.length < 2) return null;

    const question = (data.questions ?? []).find(item => item.key === rules.tie_breaker_question);
    if (!question) return null;

    const option = optionOf(question, answers);
    if (!option) return null;

    let winner: string | null = null;
    let best = 0;

    tied.forEach(key => {
        const value = Number(option.scores?.[key]) || 0;
        if (value > best) {
            best = value;
            winner = key;
        }
    });

    return winner;
}

/**
 * Reads one condition against a resolved state.
 *
 * `null` means "no condition" and passes. Anything unrecognised fails closed:
 * a typo in a stored condition must not quietly turn into "always show".
 */
export function matchesCondition(condition: AdvisorCondition | null | undefined, state: AdvisorState): boolean {
    if (condition === null || condition === undefined) return true;
    if (typeof condition !== "object" || Array.isArray(condition)) return false;

    const node = condition as Record<string, any>;

    if (Array.isArray(node.all)) return node.all.every((child: AdvisorCondition) => matchesCondition(child, state));
    if (Array.isArray(node.any)) return node.any.some((child: AdvisorCondition) => matchesCondition(child, state));
    if (node.not !== undefined) return !matchesCondition(node.not, state);

    if (typeof node.dimension === "string") {
        const value = state.scores[node.dimension] ?? 0;

        if (node.within_of_top !== undefined) {
            const top = state.ranking[0]?.score ?? 0;
            return (top - value) <= (Number(node.within_of_top) || 0);
        }

        const target = Number(node.value) || 0;
        switch (node.op ?? "gte") {
            case "gt": return value > target;
            case "lt": return value < target;
            case "lte": return value <= target;
            case "eq": return value === target;
            case "ne": return value !== target;
            default: return value >= target;
        }
    }

    if (typeof node.primary === "string") return state.primary === node.primary;
    if (typeof node.secondary === "string") return state.secondary === node.secondary;
    if (typeof node.modifier === "string") return state.modifier === node.modifier;
    if (typeof node.tier === "string") return state.tier === node.tier;

    if (typeof node.flag === "string") {
        const expected = node.is === undefined ? true : Boolean(node.is);
        return Boolean(state.flags[node.flag]) === expected;
    }

    if (typeof node.answer === "string") {
        const given = state.answers[node.answer];
        if (Array.isArray(node.in)) return node.in.includes(given);
        if (typeof node.is === "string") return given === node.is;
        return Boolean(given);
    }

    if (typeof node.low_confidence === "boolean") return state.low_confidence === node.low_confidence;

    return false;
}

/**
 * The questions that are part of the test right now.
 *
 * A conditional question counts only once its condition already holds, which is
 * what keeps the counter honest: it can grow when an answer opens a branch, and
 * it never promises a question the visitor may not reach.
 */
export function eligibleQuestions(
    questions: AdvisorQuestion[],
    state: AdvisorState,
    fromIndex = 0,
): AdvisorQuestion[] {
    return (questions ?? [])
        .slice(fromIndex)
        .filter(question => isUsableQuestion(question) && matchesCondition(question.show_when, state));
}

/** The result the state lands on. Array order decides; the first match wins. */
export function matchResult(data: AdvisorData, state: AdvisorState): AdvisorResult | null {
    const results = (data.results ?? []).filter(result => result?.show_in_site !== false);
    return results.find(result => matchesCondition(result.when, state)) ?? null;
}

/**
 * The lines explaining which answers produced the primary route.
 *
 * Only answers that actually scored the winning dimension are eligible, so the
 * explanation can never name a question that had nothing to do with the result.
 */
export function whyLines(
    answered: AdvisorQuestion[],
    state: AdvisorState,
    limit: number,
): string[] {
    if (!state.primary || limit <= 0) return [];

    const lines: string[] = [];

    answered.forEach(question => {
        if (lines.length >= limit) return;

        const option = optionOf(question, state.answers);
        if (!option) return;
        if ((Number(option.scores?.[state.primary as string]) || 0) <= 0) return;

        const line = option.why?.trim() || option.label?.trim();
        if (line) lines.push(line);
    });

    return lines;
}

/**
 * The candidates to show for the tier the visitor picked.
 *
 * A tier that matches nothing falls back to the whole list rather than to an
 * empty box: "there is nothing for you" is the one answer the advisor must
 * never give.
 */
export function candidatesForTier(candidates: AdvisorCandidate[], tier: string | null): AdvisorCandidate[] {
    const list = candidates ?? [];
    const matched = list.filter(candidate => !candidate.tier || candidate.tier === tier);
    return matched.length ? matched : list;
}
